/**
 * Formules & options YumLink (modèle freemium — décision produit 2026-06-17).
 *
 * Logique 100 % PURE (catalogue + helpers) → testable, aucune I/O.
 * Sert à :
 *  - afficher les formules (Gratuit / Medium / Premium) + prix,
 *  - afficher la « vitrine » d'un module verrouillé (KPIs, bénéfices, prix solo),
 *  - savoir quelles formules incluent un module.
 *
 * Modèle : le SITE est GRATUIT (commission 0,99 €/commande en ligne sur tous les
 * paliers). Les modules « marketing » sont des OPTIONS payantes, désactivées par
 * défaut, activées depuis le super admin (entitlements `tenant_features`).
 */

export type PlanKey = 'gratuit' | 'medium' | 'premium';

/** Clé de module optionnel (= clé d'entitlement côté `tenant_features`). */
export type ModuleKey = 'loyalty' | 'reviews' | 'campaigns' | 'yumlink_controle';

export interface PlanDef {
  key: PlanKey;
  label: string;
  /** Prix d'abonnement affiché. */
  priceLabel: string;
  /** Accroche commerciale. */
  tagline: string;
  /** Modules optionnels inclus dans la formule. */
  modules: ModuleKey[];
  /** Mise en avant visuelle (formule recommandée). */
  highlight?: boolean;
}

export interface ModuleKpi {
  value: string;
  label: string;
  /** Classe Tailwind de couleur de la valeur (accent attractif). */
  color: string;
}

export interface ModuleOption {
  key: ModuleKey;
  emoji: string;
  title: string;
  /** Libellé court (cartes formules, listes compactes). */
  shortLabel: string;
  /** « Pourquoi souscrire » — accroche bénéfice. */
  tagline: string;
  /** Bénéfices concrets (puces). */
  benefits: string[];
  /** KPIs attractifs (chiffres d'impact). */
  kpis: ModuleKpi[];
  /** Prix à la carte (option seule). */
  soloPriceLabel: string;
  /** Formules qui incluent ce module. */
  includedIn: PlanKey[];
}

/** Commission appliquée à TOUTES les formules (socle de revenu). */
export const ONLINE_ORDER_FEE_LABEL = '0,99 €/commande en ligne';

export const PLANS: PlanDef[] = [
  {
    key: 'gratuit',
    label: 'Gratuit',
    priceLabel: '0 €/mois',
    tagline: 'Votre site Click & Collect, gratuit. Vous ne payez que 0,99 € par commande en ligne.',
    modules: [],
  },
  {
    key: 'medium',
    label: 'Medium',
    priceLabel: '39 €/mois',
    tagline: 'Fidélisez vos clients et boostez vos avis Google.',
    modules: ['loyalty', 'reviews'],
    highlight: true,
  },
  {
    key: 'premium',
    label: 'Premium',
    priceLabel: '199 €/mois',
    tagline: 'Tout YumLink : fidélité, avis, campagnes marketing et YumLink Contrôle.',
    modules: ['loyalty', 'reviews', 'campaigns', 'yumlink_controle'],
  },
];

export const MODULE_OPTIONS: ModuleOption[] = [
  {
    key: 'loyalty',
    emoji: '🎁',
    title: 'Programme de fidélité',
    shortLabel: 'Fidélité',
    tagline: 'Transformez vos clients en habitués qui reviennent encore et encore.',
    benefits: [
      'Cagnotte de points automatique à chaque commande',
      'Récompense offerte au bout de X commandes',
      'Tampon fidélité directement sur la tablette caisse',
    ],
    kpis: [
      { value: '+35 %', label: 'de clients qui reviennent', color: 'text-emerald-600' },
      { value: '×2', label: 'de fréquence d’achat', color: 'text-indigo-600' },
    ],
    soloPriceLabel: '19,99 €/mois',
    includedIn: ['medium', 'premium'],
  },
  {
    key: 'reviews',
    emoji: '⭐',
    title: 'Avis Google & Satisfaction',
    shortLabel: 'Avis Google',
    tagline: 'Plus d’avis 5★ sur Google, des réponses automatiques, et un rapport mensuel qui vous dit quoi améliorer.',
    benefits: [
      '🪧 Carte NFC + QR code fournie : vos clients laissent un avis en un geste (téléphone approché ou QR scanné)',
      '🤖 Réponses automatiques à vos avis Google Business — image pro, zéro temps perdu',
      '🔒 Clients mécontents détectés en privé (NPS) avant qu’ils ne postent un avis négatif',
      '📄 Rapport PDF chaque mois : nombre d’avis par étoile, analyse de votre performance et les problèmes qui reviennent le plus à corriger',
    ],
    kpis: [
      { value: '+150 %', label: 'd’avis Google récoltés', color: 'text-amber-600' },
      { value: '4,8★', label: 'note moyenne visée', color: 'text-emerald-600' },
      { value: '1 / mois', label: 'rapport PDF de performance', color: 'text-indigo-600' },
    ],
    soloPriceLabel: '29,99 €/mois',
    includedIn: ['medium', 'premium'],
  },
  {
    key: 'campaigns',
    emoji: '📣',
    title: 'Campagnes marketing (SMS & WhatsApp)',
    shortLabel: 'Campagnes SMS & WhatsApp',
    tagline: 'Faites revenir vos clients par SMS et WhatsApp ciblés sur votre base — fini les tracts papier et le gaspillage.',
    benefits: [
      '📱 Campagnes SMS + WhatsApp (et email) ciblées sur votre base clients (CRM)',
      '🎯 Segments prêts à l’emploi : fidèles, dormants, nouveaux — le bon message à la bonne personne',
      '🌱 100 % digital et mesurable : fini les flyers distribués au hasard et jetés',
      '📊 Suivi des retours : messages envoyés, clients revenus, commandes générées',
    ],
    kpis: [
      { value: '+22 %', label: 'de commandes répétées', color: 'text-emerald-600' },
      { value: '200', label: 'SMS/WhatsApp inclus / mois', color: 'text-indigo-600' },
      { value: '0', label: 'tract papier gaspillé', color: 'text-amber-600' },
    ],
    soloPriceLabel: '99 €/mois (200 SMS/WhatsApp inclus)',
    includedIn: ['premium'],
  },
  {
    key: 'yumlink_controle',
    emoji: '🛡️',
    title: 'YumLink Contrôle',
    shortLabel: 'YumLink Contrôle',
    tagline: 'Soyez prêt en cas de contrôle fiscal — et stoppez les pertes : caisse, TPE, stock, pertes et offerts sous contrôle.',
    benefits: [
      '10+ points de contrôle quotidiens : écart caisse, écart TPE, annulations, remboursements, pertes, offerts, écart de stock, panier moyen…',
      'Traçabilité complète : un dossier carré, prêt à présenter en cas de contrôle fiscal (URSSAF, impôts)',
      'Alertes automatiques 🔴 dès qu’un écart sort des seuils (fraude, vol, gaspillage)',
      'Clôture de caisse + contrôle de stock hebdo + journal des incidents — statut clair chaque jour 🟢🟠🔴',
    ],
    kpis: [
      { value: '10+', label: 'points de contrôle / jour', color: 'text-indigo-600' },
      { value: '100 %', label: 'traçable — prêt pour le contrôle fiscal', color: 'text-emerald-600' },
    ],
    soloPriceLabel: '149 €/mois',
    includedIn: ['premium'],
  },
];

/** Retourne la définition d'un module par sa clé. */
export function getModuleOption(key: ModuleKey): ModuleOption | undefined {
  return MODULE_OPTIONS.find((m) => m.key === key);
}

/** Libellés des formules qui incluent un module (ex. ["Medium", "Premium"]). */
export function plansIncludingLabels(key: ModuleKey): string[] {
  const opt = getModuleOption(key);
  if (!opt) return [];
  return PLANS.filter((p) => opt.includedIn.includes(p.key)).map((p) => p.label);
}

/** Modules inclus par une formule (pour appliquer un preset depuis le super admin). */
export function modulesForPlan(plan: PlanKey): ModuleKey[] {
  return PLANS.find((p) => p.key === plan)?.modules ?? [];
}

/** Une ligne « service contenu » d'une formule (pour rappel dans les cartes). */
export interface PlanContentLine {
  /** Clé du module, ou null pour une ligne de socle (site / commande en ligne). */
  key: ModuleKey | null;
  emoji: string;
  label: string;
}

/**
 * Contenu lisible d'une formule : le socle commun (site + commande en ligne) PUIS
 * les options incluses (libellé court). Sert à rappeler, dans chaque carte de formule,
 * ce qu'elle contient vraiment.
 */
export function planContents(plan: PlanKey): PlanContentLine[] {
  const base: PlanContentLine[] = [
    { key: null, emoji: '🛍️', label: 'Site Click & Collect' },
    { key: null, emoji: '🧾', label: `Commande en ligne (${ONLINE_ORDER_FEE_LABEL})` },
  ];
  const mods = modulesForPlan(plan).map((k) => {
    const m = getModuleOption(k);
    return { key: k, emoji: m?.emoji ?? '•', label: m?.shortLabel ?? k };
  });
  return [...base, ...mods];
}
